import { Link } from 'react-router-dom';
import { Instagram, Youtube, Linkedin, MessageCircle, ArrowRight } from 'lucide-react';
import { CONTACT, SOCIAL_LINKS } from '../data/config';

const NAV = [
  { to: '/about', label: 'About' },
  { to: '/career-guidance', label: 'Career Guidance' },
  { to: '/career-paths', label: 'Career Paths' },
  { to: '/resources', label: 'Resources' },
  { to: '/contact', label: 'Contact' },
  { to: '/privacy', label: 'Privacy Policy' },
  { to: '/terms', label: 'Terms & Conditions' },
];

const SOCIALS = [
  { icon: Instagram, label: 'Instagram', testid: 'social-instagram', href: SOCIAL_LINKS.instagram },
  { icon: Youtube, label: 'YouTube', testid: 'social-youtube', href: SOCIAL_LINKS.youtube },
  { icon: Linkedin, label: 'LinkedIn', testid: 'social-linkedin', href: SOCIAL_LINKS.linkedin },
  { icon: MessageCircle, label: 'WhatsApp', testid: 'social-whatsapp', href: SOCIAL_LINKS.whatsapp },
];

const Footer = () => (
  <footer className="bg-navy pb-32 pt-20 text-white md:pb-10" data-testid="site-footer">
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <div className="grid gap-12 md:grid-cols-12">
        <div className="md:col-span-5">
          <Link to="/" className="inline-flex items-center rounded-2xl bg-white px-4 py-2.5" data-testid="footer-logo">
            <img src="/logo-wordmark.png" alt="CareerWithRaaj" className="h-8 w-auto" data-testid="footer-logo-image" />
          </Link>
          <p className="mt-5 max-w-sm text-sm leading-relaxed text-white/60">
            Build Your Career With Clarity. Right Career. Right Skills. Right Direction.
          </p>
          <div className="mt-6 flex gap-3">
            {SOCIALS.map(({ icon: Icon, label, testid, href }) => (
              <a
                key={label}
                href={href || '#'}
                target={href ? '_blank' : undefined}
                rel={href ? 'noopener noreferrer' : undefined}
                title={href ? label : `${label} — [${label.toUpperCase()} LINK]`}
                aria-label={href ? label : `${label} (link coming soon)`}
                data-testid={testid}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white/70 transition-[background-color,color,transform] duration-200 hover:-translate-y-1 hover:bg-coral hover:text-white"
              >
                <Icon className="h-4.5 w-4.5" />
              </a>
            ))}
          </div>
          <p className="mt-6 font-mono text-xs text-white/40">
            <a href={`mailto:${CONTACT.email}`} data-testid="footer-email-link" className="transition-colors hover:text-coral">{CONTACT.email}</a>
            {' · '}
            <a href={`tel:${CONTACT.phone.replace(/\s+/g, '')}`} data-testid="footer-phone-link" className="transition-colors hover:text-coral">{CONTACT.phone}</a>
          </p>
        </div>

        <div className="md:col-span-3">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-white/40">Explore</p>
          <ul className="mt-5 space-y-3">
            {NAV.map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  data-testid={`footer-link-${link.label.toLowerCase().replace(/[\s&]+/g, '-')}`}
                  className="text-sm text-white/70 transition-colors hover:text-coral"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-white/40">Get Started</p>
          <p className="mt-5 text-sm leading-relaxed text-white/60">
            Confused about what to study next? Get a clear, step-by-step career roadmap built around you.
          </p>
          <Link
            to="/career-roadmaps"
            data-testid="footer-start-journey-cta"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-coral px-6 py-3 text-sm font-semibold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-coral-deep"
          >
            Start Your Career Journey <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <div className="mt-16 flex flex-col gap-3 border-t border-white/10 pt-8 text-xs text-white/40 md:flex-row md:items-center md:justify-between">
        <p>© {new Date().getFullYear()} CareerWithRaaj. All rights reserved.</p>
        <p>Career Guidance • Skill Development • Career Planning</p>
      </div>
    </div>
  </footer>
);

export default Footer;
